import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { from, Observable, of, tap } from 'rxjs';
import { RootState } from '..';
import { AzureMonitoringService } from '../../services/AzureMonitoringService';
import { DocumentRestService } from '../../services/rest/document.rest.service';
import { OrderRestService } from '../../services/rest/order.rest.service';
import { util } from '../../util/util';
import { AbstractEffects } from '../AbstractEffects';
import { CalculatedTruckLoad, OrderDetail } from '../edit-florder-detail/florder-detail.interface';
import * as actions from './order-detail.actions';
import { OrderDetailCCRDocumentAction, OrderDetailCreateEditOrderPlanningAction, OrderDetailCreateRemoveMaterialAction, OrderDetailCreateSaveMaterialStartAction, OrderDetailDocumentDownloadAction, OrderDetailOpenAction, OrderDetailRequestDeliveryDatesAction, OrderDetailSaveOrderDetailAction } from './order-detail.actions';
import { RecurrenceDates } from './order-detail.interface';
import { catchError, debounceTime, map, switchMap, withLatestFrom } from 'rxjs/operators';
import { NotificationService } from 'app/core/services/notification.service';

@Injectable({ providedIn: 'root' })
export class OrderDetailEffects extends AbstractEffects {

  loadOrder$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType<OrderDetailOpenAction>(actions.ORDERDETAIL_OPEN),
      debounceTime(50),
      map((action: OrderDetailOpenAction) => action.payload),
      withLatestFrom(this.store, (payload, state) => {
        return {
          payload,
          state,
        };
      }),
      switchMap(({ payload, state }) => {
        return this.orderRestService.getOrder(payload.etmOrderNumber, payload.salesOrderNumber, payload.customerIds).pipe(
          map(
            result =>
              new actions.OrderDetailOpenSuccessAction({
                orderDetail: result,
                definitions: state.definitions,
              })
          ),
          catchError(err => this.handleFail(err, 'ORDERS.DETAIL.LABELS.OPEN_ORDER', [of(new actions.OrderDetailOpenFailAction(err))]))
        );
      })
    )
  );

  triggerRequestDeliveryDates$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType<OrderDetailCreateEditOrderPlanningAction>(actions.ORDERDETAIL_CREATE_EDIT_ORDER_PLANNING),
      withLatestFrom(this.store, (action, state) => {
        return { state };
      }),
      switchMap(({ state }) => {
        const newActions = [];
        if (!state.editOrderDetail.mode.createTemplate && !state.editOrderDetail.mode.editTemplate) {
          newActions.push(new actions.OrderDetailRequestDeliveryDatesAction(new actions.OrderDetailCreateEditOrderPlanningGetDatesSuccessAction()));
        } else {
          newActions.push(new actions.OrderDetailCreateEditOrderPlanningGetDatesSuccessAction());
        }
        return newActions;
      })
    )
  );

  triggerRequestDeliveryDatesWhenSavingOrderDetails$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType<OrderDetailSaveOrderDetailAction>(actions.ORDERDETAIL_CREATE_SAVE_ORDER_DETAIL),
      withLatestFrom(this.store, (action, state) => {
        return {
          action,
          state,
        };
      }),
      switchMap(({ action, state }: { action: OrderDetailSaveOrderDetailAction; state: RootState }) => {
        const newActions = [];
        if (!state.editOrderDetail.mode.createTemplate && !state.editOrderDetail.mode.editTemplate) {
          if (action.payload.preventGetDeliveryDatesCall) {
            newActions.push(new actions.OrderDetailSaveOrderDetailGetDatesSuccessAction());
          } else {
            newActions.push(new actions.OrderDetailRequestDeliveryDatesAction(new actions.OrderDetailSaveOrderDetailGetDatesSuccessAction()));
          }
        } else {
          newActions.push(new actions.OrderDetailSaveOrderDetailGetDatesSuccessAction());
        }
        return newActions;
      })
    )
  );

  // FlowDetailCreateSetDeliveryMethod
  requestDeliveryDates$: Observable<any> = createEffect(() =>
    this.actions$.pipe(
      ofType<OrderDetailRequestDeliveryDatesAction>(actions.ORDERDETAIL_REQUEST_DELIVERY_DATES),
      debounceTime(50),
      withLatestFrom(this.store, (action: OrderDetailRequestDeliveryDatesAction, state) => {
        return {
          type: action.type,
          payload: action.payload,
          state,
        };
      }),
      switchMap(({ type, payload, state }) => {
        return this.orderRestService.getDeliveryDates(state.editOrderDetail.orderDetail.deliveryMethod).pipe(
          switchMap(result => {
            if (payload) {
              return from([new actions.OrderDetailRequestDeliveryDatesSuccessAction(result), payload]);
            } else {
              return from([new actions.OrderDetailRequestDeliveryDatesSuccessAction(result)]);
            }
          }),
          catchError(error => {
            return this.handleFail(error, 'ORDERS.DETAIL.LABELS.GET_DELIVERY_DATES', [of(new actions.OrderDetailRequestDeliveryDatesFailAction(error))]);
          })
        );
      })
    )
  );

  // ORDERDETAIL_CREATE_START
  calculateAfterRemoveMaterial$ = createEffect(() =>
    this.actions$.pipe(
      ofType<OrderDetailCreateRemoveMaterialAction>(actions.ORDERDETAIL_CREATE_DELETE_MATERIAL),
      withLatestFrom(this.store, (action: OrderDetailCreateRemoveMaterialAction, state) => {
        return {
          payload: action.payload,
          state,
        };
      }),
      switchMap(({ payload, state }) => {
        const orderDetail = state.editOrderDetail.orderDetail;

        const restCall: (orderDetail: OrderDetail) => Observable<CalculatedTruckLoad> =
          state.editOrderDetail.mode.createTemplate || state.editOrderDetail.mode.editTemplate
            ? this.orderRestService.checkPalletFloorQuantitySuccessDummy.bind(this.orderRestService)
            : this.orderRestService.checkPalletFloorQuantity.bind(this.orderRestService);

        return restCall(orderDetail).pipe(
          switchMap((result: CalculatedTruckLoad) => {
            const returnActions: Action[] = [];
            this.checkPalletFloorQuantitySuccess(
              null,
              (result, warningAction) => {
                // warningAction = crappy solution to a late fix just before PROD-transport for BE/NL
                returnActions.push(new actions.OrderDetailLoadTruckCalculateSuccessAction(result));
                if (warningAction) {
                  returnActions.push(warningAction);
                } else {
                  this.notificationService.showToastr({
                    type: 'success',
                    messageCode: 'ORDERS.MATERIAL.NOTIFICATIONS.MATERIAL_REMOVED',
                    disableClose: false,
                  });
                }
              },
              result
            );
            return from(returnActions);
          }),
          catchError(err => this.handleFail(err, 'ERRORS.ORDER.CREATE.TRUCK_LOAD', [of(new actions.OrderDetailLoadTruckCalculateFailAction(err))]))
        );
      })
    )
  );

  calculateLoadAddMaterial$ = createEffect(() =>
    this.actions$.pipe(
      ofType<OrderDetailCreateSaveMaterialStartAction>(actions.ORDERDETAIL_CREATE_SAVE_MATERIAL_START),
      withLatestFrom(this.store, (action: OrderDetailCreateSaveMaterialStartAction, state) => {
        return {
          payload: action.payload,
          state,
        };
      }),
      switchMap(({ payload, state }) => {
        const orderDetail = util.deepCopy(state.editOrderDetail.orderDetail);
        const material = payload.material;
        const materialExists = orderDetail.materials.findIndex(m => m.internalId === material.internalId);
        if (materialExists >= 0) {
          orderDetail.materials.splice(materialExists, 1, material);
        } else {
          orderDetail.materials.push(material);
        }

        const actionsArray: Action[] = [];
        const restrictions = state.customerInfo.restrictions;
        if (payload.preventRestCall) {
          actionsArray.push(
            new actions.OrderDetailCreateSaveMaterialSuccessAction({
              calculation: null,
              material,
              restrictions,
            })
          );
          if (payload.showPopup) {
            this.notificationService.showToastr({
                type: 'success',
                messageCode: 'ORDERS.MATERIAL.NOTIFICATIONS.MATERIAL_ADDED',
                disableClose: false,
              });
          }
          return from(actionsArray);
        } else {
          return this.orderRestService.checkPalletFloorQuantity(orderDetail).pipe(
            switchMap(
              this.checkPalletFloorQuantitySuccess.bind(this, material, (result, warningAction) => {
                const restrictions = state.customerInfo.restrictions;
                actionsArray.push(
                  new actions.OrderDetailCreateSaveMaterialSuccessAction({
                    calculation: result as CalculatedTruckLoad,
                    material,
                    restrictions,
                  })
                );
                if (payload.showPopup) {
                  if (warningAction) {
                    actionsArray.push(warningAction);
                  } else {
                    this.notificationService.showToastr({
                      type: 'success',
                      messageCode: 'ORDERS.MATERIAL.NOTIFICATIONS.MATERIAL_ADDED',
                      disableClose: false,
                    });
                  }
                }
                return from(actionsArray);
              })
            ),
            catchError(err =>
              this.handleFail(err, 'ERRORS.ORDER.CREATE.TRUCK_LOAD', [
                of(
                  new actions.OrderDetailCreateSaveMaterialFailAction({
                    error: err,
                    material,
                  })
                ),
              ])
            )
          );
        }
      })
    )
  );

  copyOrderNoValidContract$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.ORDERDETAIL_COPY_OPEN_ORDER_NO_VALID_CONTRACT),
      tap(() =>
          this.notificationService.showModal({
            type: 'error',
            messageCode: 'ERRORS.COPY_ORDER.NO_VALID_CONTRACT',
            disableClose: false,
          })
      )
    ), {dispatch: false}
  );

  downloadDocument$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType<OrderDetailDocumentDownloadAction>(actions.ORDERDETAIL_DOCUMENT_DOWNLOAD),
      debounceTime(50),
      withLatestFrom(this.store, (action, state) => {
        return { state, action };
      }),
      switchMap(({ action, state }: { action: OrderDetailDocumentDownloadAction; state: RootState }) => {
        const customerIds = state.session.userContext.isSuperUser ? state.session.userContext.customers.map(c => c.id) : [];
        return this.documentRestService.getDocument(action.payload.documentId, customerIds).pipe(
          map((blob: Blob) => {
            return new actions.OrderDetailDocumentDownloadSuccessAction({
              filename: `order.${util.getExtension(blob.type)}`,
              blob: blob,
            });
          }),
          catchError(err => this.handleFail(err, `ERRORS.${action.payload.extension?.toUpperCase()}_DOWNLOAD_FAILED.TITLE`, [of(new actions.OrderDetailDocumentDownloadFailAction(err))]))
        );
      })
    )
  );

  downloadCCRDocument$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType<OrderDetailCCRDocumentAction>(actions.ORDERDETAIL_CCR_DOCUMENT_DOWNLOAD),
      debounceTime(50),
      switchMap((action: OrderDetailCCRDocumentAction) =>
        this.documentRestService.getCCRDocument(action.payload.documentId).pipe(
          map((blob: Blob) => {
            return new actions.OrderDetailCCRDocumentSuccessAction({
              filename: `order.${util.getExtension(blob.type)}`,
              blob,
            });
          }),
          catchError(err => this.handleFail(err, `ERRORS.${action.payload.extension?.toUpperCase()}_DOWNLOAD_FAILED.TITLE`, [of(new actions.OrderDetailCCRDocumentFailAction(err))]))
        )
      )
    )
  );

  getRecurrenceDates: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.ORDERDETAIL_GET_RECURRENCE_DATES),
      debounceTime(50),
      withLatestFrom(this.store, (action, state) => {
        return state;
      }),
      switchMap((state: RootState) =>
        this.orderRestService.getRecurrenceDates(state.editOrderDetail.orderDetail).pipe(
          map((data: RecurrenceDates) => {
            return new actions.OrderDetailGetRecurrenceDatesSuccessAction(data);
          }),
          catchError(err => this.handleFail(err, `ERRORS.GET_RECURRENCE_DATES.TITLE`, [of(new actions.OrderDetailGetRecurrenceDatesFailAction(err))]))
        )
      )
    )
  );

  /*
   Start calculation when material is added / removed / edited
   */

  checkPalletFloorQuantitySuccess(material, successObservables, result): Observable<Action> {
    const overload = result.message && (result.message.type === 'E' || result.message.type === 'I' || result.message.type === 'W');
    let warningAction = null;
    const type = result.message && result.message.type === 'E' ? 'warning' : 'info';
    if (overload) {
      if (type === 'warning') {
        // material can't be added, a warning is shown
        this.notificationService.showModal({
            type,
            message: result.message.description,
            disableClose: false,
          }
        );
        return from([
          new actions.OrderDetailCreateSaveMaterialOverloadAction({
            material,
            calculation: result as CalculatedTruckLoad,
          }),
        ]);
      } else {
        // Material will be added but user is informed of max truckload
        this.notificationService.showToastr({
          type,
          message: result.message.description,
          disableClose: false,
        });
      }
    }
    return successObservables(result); // crappy implementation
  }

  constructor(
    private store: Store<RootState>,
    azureMonitoringService: AzureMonitoringService,
    private actions$: Actions,
    private orderRestService: OrderRestService,
    private documentRestService: DocumentRestService,
    translateService: TranslateService,
    public dialog: MatDialog,
    notificationService: NotificationService
  ) {
    super(translateService, azureMonitoringService, notificationService);
  }
}
